import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const Dhvajarohan26JanForm = () => {
  const { language } = useLanguage();
  const componentRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: language === 'marathi' ? 'Dhvajarohan_26_Jan_Letter.pdf' : 'Republic_Day_Flag_Hoisting_Letter.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white flex flex-col items-center">
      <style>
        {`
          .page {
            width: 210mm;
            height: 297mm;
            padding: 20mm 20mm;
            margin: 0 auto;
            box-sizing: border-box;
            background-color: white;
            box-shadow: 0 0 15px rgba(0,0,0,0.1);
            position: relative;
          }
          
          .dashed-line {
            border-bottom: 1px dashed black;
            display: inline-block;
          }

          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              background-color: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .page {
              margin: 0 !important;
              box-shadow: none !important;
              width: 210mm !important;
              height: 297mm !important;
              border: none !important;
            }
            .no-print {
              display: none !important;
            }
            .min-h-screen {
              min-height: auto !important;
              padding: 0 !important;
            }
          }
        `}
      </style>
      
      {/* Controls Container */}
      <div className="w-full max-w-[210mm] mb-6 flex justify-end space-x-4 print:hidden px-4 no-print">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Print' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Download PDF' : 'Download PDF'}
        </button>
      </div>

      <div ref={componentRef} className="page text-black text-[15px] leading-[1.7] relative flex flex-col pt-8 px-6">
         
         <div className="flex justify-between mb-8">
            <div className="flex flex-col space-y-1">
               <span>{language === 'marathi' ? 'प्रति,' : 'To,'}</span>
               <span className="font-bold">{language === 'marathi' ? 'मा.मुख्याध्यापक,' : 'Hon. Headmaster,'}</span>
               <div className="flex items-end">
                  <span className="whitespace-nowrap">{language === 'marathi' ? 'जि.प.प्रा.शा.' : 'Z.P.P. School'}</span>
                  <span className="dashed-line w-[180px] h-4 mx-1"></span>
               </div>
            </div>
            <div>
               <div className="flex items-center">
                  <span>{language === 'marathi' ? 'दिनांक:------/०१/२०' : 'Date:------/01/20'}</span>
               </div>
            </div>
         </div>

         <div className="flex flex-col items-center justify-center font-bold text-[16px] mb-8 text-center space-y-2 px-8">
            <div className="flex items-center justify-center w-full">
               <span className="whitespace-nowrap">{language === 'marathi' ? 'विषय: २६ जानेवारी २०---- प्रजासत्ताकदिनी ग्रामपंचायत कार्यालय' : 'Subject: Organizing flag hoisting program on 26th Jan 20---- Republic Day at Gram Panchayat Office'}</span>
               <span className="dashed-line flex-grow h-4 mx-2"></span>
            </div>
            <div>{language === 'marathi' ? 'येथील ध्वजारोहण कार्यक्रम आयोजन करणेबाबत.' : ''}</div>
         </div>

         <div className="font-bold mb-2">{language === 'marathi' ? 'मा.महोदय,' : 'Respected Sir/Madam,'}</div>
         <p className="indent-12 text-justify mb-4">
            {language === 'marathi' ? (
              <>
                उपरोक्त विषयी कळविण्यात येते की, माझ्याकडे <span className="dashed-line w-[220px]"></span> व <span className="dashed-line w-[150px]"></span> या दोन ग्रामपंचायतीचा पदभार आहे. २६ जानेवारी २०----- प्रजासत्ताक दिनाच्या दिवशी ध्वजारोहण कार्यक्रमासाठी मी <span className="dashed-line w-[220px]"></span> या ग्रामपंचायत कार्यालयात हजर राहणार आहे. दोन्ही ग्रामपंचायतीला मला उपस्थित राहणे शक्य नसल्यामुळे ग्रामपंचायत कार्यालय <span className="dashed-line w-[220px]"></span> येथील ध्वजारोहण कार्यक्रम आयोजन व पूर्वतयारी (जसे ध्वज ध्वजस्तंभावर बांधणे, सन्मानपूर्वक उतरवणे इत्यादी) आपण किंवा आपल्या अधिनस्त शिक्षक यांना जबाबदारी देऊन ध्वजारोहण कार्यक्रम आयोजन करावे, ही विनंती.
              </>
            ) : (
              <>
                With reference to the above subject, this is to inform you that I have the charge of two Gram Panchayats <span className="dashed-line w-[220px]"></span> and <span className="dashed-line w-[150px]"></span>. On 26th January 20---- Republic Day, I will be present for the flag hoisting program at <span className="dashed-line w-[220px]"></span> Gram Panchayat office. As it is not possible for me to be present at both Gram Panchayats, it is requested that you or your subordinate teachers take the responsibility for organizing and preparing the flag hoisting program (such as tying the flag to the pole, lowering it respectfully, etc.) at Gram Panchayat office <span className="dashed-line w-[220px]"></span>.
              </>
            )}
         </p>
         
         <p className="indent-12 text-justify mb-4">
            {language === 'marathi' ? (
              <>
                ग्रामपंचायत कार्यालय येथील ध्वजारोहण ग्रामपंचायत सरपंच श्री/श्रीमती <span className="dashed-line w-[280px]"></span><br/>
                <span className="dashed-line w-[250px]"></span> यांच्या हस्ते करण्यात येईल.
              </>
            ) : (
              <>
                The flag hoisting at the Gram Panchayat office will be done by the hands of Gram Panchayat Sarpanch Mr./Mrs. <span className="dashed-line w-[280px]"></span><br/>
                <span className="dashed-line w-[250px]"></span>.
              </>
            )}
         </p>

         <p className="indent-12 mb-12">
            {language === 'marathi' ? 'करिता आपण या कार्यक्रमाचे आयोजन व पूर्वतयारी करावी ही विनंती.' : 'Therefore, you are requested to organize and prepare for this program.'}
         </p>

         <div className="flex justify-end mb-16 pr-8">
            <div className="flex flex-col items-start w-56">
               <div className="mb-12 w-full text-left">{language === 'marathi' ? 'आपला विश्वासू,' : 'Yours faithfully,'}</div>
               <div className="font-bold w-full text-center">{language === 'marathi' ? 'ग्रामपंचायत अधिकारी' : 'Gram Panchayat Officer'}</div>
               <div className="flex items-end w-full mt-1">
                  <span className="whitespace-nowrap">{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'}</span>
                  <span className="dashed-line flex-grow h-4 ml-1"></span>
               </div>
            </div>
         </div>

         <div className="flex flex-col mb-6 font-bold">
            <span className="mb-1">{language === 'marathi' ? 'सोबत:' : 'Enclosure:'}</span>
            <span className="mb-1 font-normal">{language === 'marathi' ? '१) राष्ट्रध्वज' : '1) National Flag'}</span>
            <span className="font-normal">{language === 'marathi' ? '२)' : '2)'}</span>
         </div>

         <div className="flex flex-col space-y-2 font-bold">
            <span className="mb-1">{language === 'marathi' ? 'प्रतिलिपी:' : 'Copy to:'}</span>
            <div className="flex items-end font-normal w-full">
               <span className="whitespace-nowrap">{language === 'marathi' ? '१) मा.गटविकास अधिकारी, पंचायत समिती' : '1) Hon. Block Development Officer, Panchayat Samiti'}</span>
               <span className="dashed-line flex-grow h-4 mx-2"></span>
               <span className="whitespace-nowrap">{language === 'marathi' ? 'यांना माहितीस्तव सविनय सादर.' : 'for information.'}</span>
            </div>
            <div className="flex items-end font-normal w-full mt-1">
               <span className="whitespace-nowrap">{language === 'marathi' ? '२) मा. सरपंच/उपसरपंच/' : '2) Hon. Sarpanch/Deputy Sarpanch/'}</span>
               <span className="dashed-line flex-grow h-4 mx-1"></span>
               <span className="whitespace-nowrap">{language === 'marathi' ? 'यांना माहितीस्तव.' : 'for information.'}</span>
            </div>
         </div>

      </div>
    </div>
  );
};

export default Dhvajarohan26JanForm;
